import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Storefront, ForkKnife } from "@phosphor-icons/react";
import AppleSignInButton from "../components/AppleSignInButton";
import PageMeta from "../components/PageMeta";

export default function Signup() {
  const { register, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(
    params.get("role") === "owner" || params.get("claim") ? "owner" : "customer",
  );
  const [name, setName] = useState("");
  const [truckName, setTruckName] = useState("");
  const [homeBase, setHomeBase] = useState(params.get("home_base") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // `next` query param survives signup so the claim-CTA flow can bounce
  // straight back to /upgrade (or /t/:slug) once the account exists.
  const nextUrl = params.get("next") || null;
  // `claim` (format: `city/slug`) — when present, automatically fires
  // the preloaded-truck claim POST after a successful signup so the
  // owner never has to click "Claim" twice. Comes from the "Claim this
  // truck" CTA on `/food-trucks/<city>/<slug>` for logged-out users.
  const claimParam = params.get("claim") || null;

  // Pre-fetch the truck name from the preloaded row when claim= is set,
  // so the owner sees their actual truck name in the signup form rather
  // than having to retype it.
  useEffect(() => {
    if (!claimParam || !/^[\w-]+\/[\w-]+$/.test(claimParam)) return;
    const [claimCity, claimSlug] = claimParam.split("/");
    api.get(`/preloaded-trucks/by-slug/${claimCity}/${claimSlug}`)
      .then((r) => {
        if (r?.data?.truck_name) setTruckName(r.data.truck_name);
      })
      .catch(() => { /* non-fatal */ });
  }, [claimParam]);

  // If the user is already signed in, /signup is a dead end — bounce them
  // to a sensible home (owners → dashboard, customers → map). Fixes the
  // "I'm signed in but Get Started still wants me to sign up" bug.
  useEffect(() => {
    if (authLoading || !user) return;
    if (nextUrl) navigate(nextUrl, { replace: true });
    else if (user.is_admin) navigate("/admin", { replace: true });
    else if (user.role === "owner") navigate("/dashboard", { replace: true });
    else navigate("/map", { replace: true });
  }, [user, authLoading, navigate, nextUrl]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (role === "owner" && !truckName.trim()) {
      toast.error("Truck name is required to register as an owner.");
      return;
    }
    setLoading(true);
    const payload = { name, email, password, role };
    if (role === "owner") {
      payload.truck_name = truckName.trim();
      if (homeBase.trim()) payload.home_base = homeBase.trim();
    }
    const res = await register(payload);
    setLoading(false);
    if (res.ok) {
      // Signup-promo Founding Pro (e.g. OBX first-5-signups) — celebrate.
      const foundingCity = res.user?.founding_pro_cities?.[0];
      if (foundingCity) {
        const cityLabel = foundingCity.city_slug === "outer-banks-nc"
          ? "OBX"
          : foundingCity.city_slug.replace(/[-_]/g, " ");
        toast.success(`🏆 You're ${cityLabel} Founding Pro #${foundingCity.slot_number}!`, {
          description: foundingCity.expires_at
            ? "Free TruckTrackr Pro for your first year — welcome aboard."
            : "Free TruckTrackr Pro for life — welcome aboard.",
          duration: 8000,
        });
      }
      // If this signup was kicked off from a "Claim this truck" CTA,
      // fire the claim immediately so the new owner lands on the
      // verified state without having to click twice.
      if (claimParam && /^[\w-]+\/[\w-]+$/.test(claimParam)) {
        try {
          const [claimCity, claimSlug] = claimParam.split("/");
          const r = await api.post(`/preloaded-trucks/${claimCity}/${claimSlug}/claim`, {});
          if (r.data?.auto_verified) {
            toast.success(`Account created & ${r.data.truck_name} claimed!`, {
              description: r.data.founding_slot_number
                ? `🏆 You're ${r.data.founding_city_slug.charAt(0).toUpperCase() + r.data.founding_city_slug.slice(1)} Founding Pro #${r.data.founding_slot_number}.`
                : undefined,
            });
          } else {
            toast.success("Account created — claim submitted for review.");
          }
        } catch (err) {
          toast.success("Account created!");
          toast.warning(formatApiError(err.response?.data?.detail) || "Claim couldn't auto-submit. Click Claim once more on the truck page.");
        }
      } else {
        toast.success("Account created!");
      }
      const to = nextUrl || (role === "owner" ? "/dashboard" : "/map");
      navigate(to);
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4 py-10" data-testid="signup-page">
      <PageMeta
        title="Sign Up — Free for Fans & Truck Owners | TruckTrackr"
        description="Create a free TruckTrackr account. Truck owners: put your truck on the live map in under a minute. Fans: follow trucks and get notified."
        canonical="https://thetrucktrackr.com/signup"
      />
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md rounded-2xl border border-border bg-card p-6 sm:p-8 space-y-5"
      >
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Create your account</h1>
          <p className="text-sm text-muted-foreground mt-1">Two quick questions, then you&apos;re in.</p>
        </div>

        <div className="grid grid-cols-2 gap-3" data-testid="signup-role-selector">
          <button
            type="button"
            onClick={() => setRole("customer")}
            className={`rounded-xl border p-4 text-left transition ${
              role === "customer"
                ? "border-primary bg-primary/10"
                : "border-border bg-background hover:border-primary/40"
            }`}
            data-testid="signup-role-customer"
          >
            <ForkKnife weight="duotone" className="h-5 w-5 text-primary" />
            <div className="mt-2 font-display font-semibold">I&apos;m hungry</div>
            <div className="text-xs text-muted-foreground mt-1">Find trucks near me</div>
          </button>
          <button
            type="button"
            onClick={() => setRole("owner")}
            className={`rounded-xl border p-4 text-left transition ${
              role === "owner"
                ? "border-primary bg-primary/10"
                : "border-border bg-background hover:border-primary/40"
            }`}
            data-testid="signup-role-owner"
          >
            <Storefront weight="duotone" className="h-5 w-5 text-primary" />
            <div className="mt-2 font-display font-semibold">I own a truck</div>
            <div className="text-xs text-muted-foreground mt-1">Post your daily spot</div>
          </button>
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">{role === "owner" ? "Your name" : "Name"}</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            data-testid="signup-name-input"
          />
        </div>

        {role === "owner" && (
          <div className="space-y-2" data-testid="signup-truckname-wrapper">
            <Label htmlFor="truckName">Truck name</Label>
            <Input
              id="truckName"
              value={truckName}
              onChange={(e) => setTruckName(e.target.value)}
              required
              maxLength={120}
              placeholder="e.g. Ember Tacos"
              data-testid="signup-truckname-input"
            />
            <p className="text-xs text-muted-foreground">
              This is the name customers will see on the map. You can edit it later.
            </p>
          </div>
        )}

        {role === "owner" && (
          <div className="space-y-2" data-testid="signup-homebase-wrapper">
            <Label htmlFor="homeBase">Home base</Label>
            <Input
              id="homeBase"
              value={homeBase}
              onChange={(e) => setHomeBase(e.target.value)}
              maxLength={120}
              placeholder="City, State — e.g. Lancaster, PA"
              data-testid="signup-homebase-input"
            />
            <p className="text-xs text-muted-foreground">
              The city your truck calls home — helps us connect you with nearby customers.
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            data-testid="signup-email-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
            data-testid="signup-password-input"
          />
          <p className="text-xs text-muted-foreground">At least 6 characters.</p>
        </div>

        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-11 rounded-full"
          data-testid="signup-submit-btn"
        >
          {loading ? "Creating…" : "Create account"}
        </Button>

        <AppleSignInButton onSuccess={() => navigate("/map", { replace: true })} />

        <p className="text-sm text-center text-muted-foreground">
          Already registered?{" "}
          <Link to="/login" className="text-primary underline-offset-4 hover:underline" data-testid="signup-to-login">
            Sign in
          </Link>
        </p>
      </form>
    </div>
  );
}
